import React, { useState, useEffect } from 'react';
import { useBed } from '../../../context/BedContext';
import { useNavigate } from 'react-router-dom';

const BedDetailModal = ({ isOpen, onClose, bed }) => {
  const { getEquipmentUsingBed, changeBedStatus } = useBed();
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);

  useEffect(() => {
    if (isOpen && bed) {
      loadEquipment();
    }
  }, [isOpen, bed]);

  const loadEquipment = async () => {
    if (!bed) return;
    setLoading(true);
    try {
      const eq = await getEquipmentUsingBed(bed.id);
      setEquipment(eq);
    } catch (err) {
      console.error('Error loading equipment:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async () => {
    if (!bed) return;

    if (bed.status === 'IN_USE') {
      if (!window.confirm(`เปลี่ยนสถานะเตียง "${bed.bedName}" เป็น "ว่าง" หรือไม่?\n⚠️ หมายเหตุ: การเปลี่ยนสถานะจะไม่เปลี่ยนสถานะอุปกรณ์ที่ใช้งานอยู่`)) {
        return;
      }

      setChangingStatus(true);
      try {
        const result = await changeBedStatus(bed.id, 'AVAILABLE');
        if (result.success) {
          alert('เปลี่ยนสถานะสำเร็จ');
          onClose();
        } else {
          alert(`เกิดข้อผิดพลาด: ${result.error}`);
        }
      } catch (err) {
        alert(`เกิดข้อผิดพลาด: ${err.message}`);
      } finally {
        setChangingStatus(false);
      }
    }
  };

  const getStatusBadge = () => {
    switch (bed?.status) {
      case 'AVAILABLE':
        return <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">🟢 ว่าง</span>;
      case 'IN_USE':
        return <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">🔴 ใช้งานอยู่</span>;
      case 'MAINTENANCE':
        return <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">🟡 ซ่อมบำรุง</span>;
      case 'UNAVAILABLE':
        return <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">⚫ ไม่พร้อมใช้งาน</span>;
      default:
        return null;
    }
  };

  if (!isOpen || !bed) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-800">🛏️ {bed.bedName}</h2>
            <p className="text-sm text-gray-500">#{bed.bedNumber}</p>
          </div>
          {getStatusBadge()}
        </div>

        {/* Bed Info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">หอผู้ป่วย</p>
              <p className="font-medium text-gray-800">{bed.ward}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">แผนก</p>
              <p className="font-medium text-gray-800">{bed.department}</p>
            </div>
            {bed.bedLocation && (
              <div>
                <p className="text-xs text-gray-500 mb-1">ตำแหน่งวางเตียง</p>
                <p className="font-medium text-gray-800">{bed.bedLocation}</p>
              </div>
            )}
            {(bed.floor || bed.room) && (
              <div>
                <p className="text-xs text-gray-500 mb-1">ที่ตั้ง</p>
                <p className="font-medium text-gray-800">
                  {bed.floor && `ชั้น ${bed.floor}`} {bed.room && `ห้อง ${bed.room}`}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Equipment List */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">
            อุปกรณ์ที่ใช้งานอยู่ ({equipment.length})
          </h3>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">กำลังโหลด...</p>
            </div>
          ) : equipment.length === 0 ? (
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <p className="text-gray-600">ไม่มีอุปกรณ์ที่ใช้งานอยู่</p>
            </div>
          ) : (
            <div className="space-y-3">
              {equipment.map((eq) => (
                <div
                  key={eq.id}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition cursor-pointer"
                  onClick={() => {
                    navigate(`/nurse/equipment/${eq.id}`);
                    onClose();
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">{eq.name}</p>
                      {eq.code && (
                        <p className="text-sm text-gray-500">รหัส: {eq.code}</p>
                      )}
                      {eq.serialNumber && (
                        <p className="text-sm text-gray-500">Serial: {eq.serialNumber}</p>
                      )}
                      {eq.inUseByName && (
                        <p className="text-sm text-gray-500 mt-1">
                          ผู้ใช้: {eq.inUseByName}
                          {eq.inUseAt && (
                            <span className="ml-2">
                              ({new Date(eq.inUseAt.toDate()).toLocaleString('th-TH')})
                            </span>
                          )}
                        </p>
                      )}
                      {eq.inUseNotes && (
                        <p className="text-sm text-gray-600 mt-1">หมายเหตุ: {eq.inUseNotes}</p>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/nurse/equipment/${eq.id}`);
                        onClose();
                      }}
                      className="ml-4 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition"
                    >
                      ดูรายละเอียด →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          {bed.status === 'IN_USE' && (
            <button
              onClick={handleStatusChange}
              disabled={changingStatus}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
            >
              {changingStatus ? 'กำลังเปลี่ยนสถานะ...' : '🔄 เปลี่ยนสถานะเป็นว่าง'}
            </button>
          )}
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
          >
            ปิด
          </button>
        </div>
      </div>
    </div>
  );
};

export default BedDetailModal;



